/**
 * EngineSummary — aggregated view of engine results.
 * Includes the "Propose" bridge (Doc 104 Steps 7→8).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Loader2, GitMerge } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { COMMIT_TO_PLAN_VERSION_LABEL, WILL_BLOCK_AUTHORIZATION_LABEL } from '../../../lib/planning/vocabulary';
import { itemsMissingWorkType } from '../../../lib/planningService';
import CommitToPlanVersionModal from './CommitToPlanVersionModal';
import type { PriorityFeasibility } from '../../../lib/planningEngineModule.types';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import { useItemHref } from '@/lib/itemLinkScope';

function feasibilityClass(f: PriorityFeasibility): string {
  switch (f) {
    case 'Fully Achievable':
      return 'text-[var(--green)] dark:text-[var(--text-ter)]';
    case 'Partially Achievable':
      return 'text-[var(--mustard)] dark:text-[var(--mustard)]';
    case 'Not Achievable':
      return 'text-[var(--coral)] dark:text-[var(--coral)]';
    case 'No Work':
      return 'text-[var(--text-ter)] dark:text-[var(--text-sec)]';
  }
}

function feasibilityBadge(f: PriorityFeasibility) {
  const cls =
    f === 'Fully Achievable'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
      : f === 'Partially Achievable'
        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
        : f === 'Not Achievable'
          ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
          : 'bg-[var(--surface)] text-[var(--text-sec)] dark:bg-[var(--bg)]/20';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{f}</span>;
}

export default function EngineSummary() {
  const itemHref = useItemHref();
  const { planOutput, running, error, runEngineForScenario, activeScenario, runBlockedReason, lastRunNotice } = usePlanningEngine();
  // Said here, before Propose, instead of at the last click on the governance
  // page: an item with no work type has no execution recipe, and the
  // authorization gate refuses the version for it (build contract §2.8, form 4).
  const [missingWorkType, setMissingWorkType] = useState<Array<{ id: string; title: string }>>([]);
  const outputStamp = planOutput?.generatedAt ?? null;
  useEffect(() => {
    if (!planOutput) {
      setMissingWorkType([]);
      return;
    }
    let cancelled = false;
    void itemsMissingWorkType(planOutput.prioritySummaries.map((p) => p.priority.id)).then(({ data }) => {
      if (!cancelled) setMissingWorkType(data);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read once per engine run
  }, [outputStamp]);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const exec = planOutput?.executiveSummary;

  function plainEnglishSummary(): string {
    if (!exec) return '';
    const {
      totalWorkUnits, includedCount, deferredCount, blockedCount, notReadyCount, needsSizingCount,
      noCapacityModelCount, overallFeasibility, participationAtRiskCount, participationBlockedCount,
    } = exec;
    const lines: string[] = [];
    lines.push(`The engine evaluated ${totalWorkUnits} planning row${totalWorkUnits !== 1 ? 's' : ''} in ${PLANNING_MODE_LABELS[planOutput!.planningMode]} mode.`);
    if (includedCount > 0) lines.push(`${includedCount} fit within available capacity and are included in the plan.`);
    if (deferredCount > 0) lines.push(`${deferredCount} are out for lack of capacity.`);
    if (blockedCount > 0) lines.push(`${blockedCount} are blocked on unsatisfied dependencies.`);
    if (needsSizingCount > 0) lines.push(`${needsSizingCount} have no size estimate yet and need sizing before they can be planned.`);
    if (notReadyCount > 0) lines.push(`${notReadyCount} are not yet in a ready state.`);
    if (noCapacityModelCount > 0) lines.push(`${noCapacityModelCount} have no capacity model defined.`);
    if (participationBlockedCount > 0) lines.push(`${participationBlockedCount} are blocked by a missing required capability.`);
    if (participationAtRiskCount > 0) lines.push(`${participationAtRiskCount} are at risk due to unconfirmed team participation.`);
    lines.push(`Overall feasibility: ${overallFeasibility}.`);
    return lines.join(' ');
  }

  return (
    <>
      {showCommitModal && planOutput && (
        <CommitToPlanVersionModal
          output={planOutput}
          prefilledCycleId={activeScenario?.planCycleId}
          onClose={() => setShowCommitModal(false)}
        />
      )}

    <div className="p-4 md:p-6 w-full h-full overflow-y-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Engine Summary</h2>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            Aggregated plan results by resource and priority.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {planOutput && (
            <button
              onClick={() => setShowCommitModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--green)] hover:bg-[var(--green)] text-white text-sm font-medium"
              title="Propose this engine output to the Governance Layer as an immutable plan version"
            >
              <GitMerge className="h-4 w-4" />
              {COMMIT_TO_PLAN_VERSION_LABEL}
            </button>
          )}
          <button
            onClick={() => void runEngineForScenario()}
            disabled={running || runBlockedReason != null}
            title={runBlockedReason ?? undefined}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Run Engine
          </button>
        </div>
      </div>

      {missingWorkType.length > 0 && (
        <div role="alert" className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3 text-sm text-[var(--text-pri)]">
          {missingWorkType.length === 1 ? '1 item has' : `${missingWorkType.length} items have`} no work type and{' '}
          {WILL_BLOCK_AUTHORIZATION_LABEL}:{' '}
          {missingWorkType.map((item, i) => (
            <span key={item.id}>
              {i > 0 && ', '}
              <Link to={itemHref(item.id)} className="font-medium underline">
                {item.title}
              </Link>
            </span>
          ))}
          . Set the work type on the item, then run the engine again before you propose.
        </div>
      )}
      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {!error && !running && runBlockedReason && (
        <p className="text-sm text-[var(--text-sec)]">{runBlockedReason}</p>
      )}

      {lastRunNotice && !running && (
        <div role="status" className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] px-3 py-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          {lastRunNotice}
        </div>
      )}

      {!planOutput ? (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-12 text-center">
          <Zap className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No results yet. Run the engine to generate a summary.
          </p>
        </div>
      ) : (
        <>
          {/* Plain English */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
            <h3 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-3">
              Summary
            </h3>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">
              {plainEnglishSummary()}
            </p>
          </div>

          {/* Key numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
            {[
              // Row count, not priority count — Work Units mode plans at
              // work-unit grain, so a decomposed priority contributes more
              // than one row here even though it's still one priority.
              { label: 'Total rows', value: exec!.totalWorkUnits, color: 'text-[var(--text-sec)] dark:text-[var(--text-pri)]' },
              { label: 'Included', value: exec!.includedCount, color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
              { label: 'Out - capacity', value: exec!.deferredCount, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]' },
              { label: 'Blocked', value: exec!.blockedCount, color: 'text-[var(--coral)] dark:text-[var(--coral)]' },
              { label: 'Needs Sizing', value: exec!.needsSizingCount, color: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' },
              { label: 'Not Ready', value: exec!.notReadyCount, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]' },
              { label: 'No Cap. Model', value: exec!.noCapacityModelCount, color: 'text-[var(--text-ter)]' },
              { label: 'Part. At Risk', value: exec!.participationAtRiskCount, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]' },
              { label: 'Part. Blocked', value: exec!.participationBlockedCount, color: 'text-[var(--coral)] dark:text-[var(--coral)]' },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-4 text-center"
              >
                <div className={`text-3xl font-bold ${item.color}`}>{item.value}</div>
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">{item.label}</div>
              </div>
            ))}
          </div>

          {/* Overall feasibility */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
            <h3 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-2">
              Overall Feasibility
            </h3>
            <div className={`text-2xl font-bold ${feasibilityClass(exec!.overallFeasibility)}`}>
              {exec!.overallFeasibility}
            </div>
          </div>

          {/* Resource feasibility table */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Resource Utilization
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                <thead className="bg-[var(--surface)] dark:bg-[var(--bg)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Resource Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Capacity WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Demand WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Remaining WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Utilization</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {planOutput.resourceSummaries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                        No resource data.
                      </td>
                    </tr>
                  )}
                  {planOutput.resourceSummaries.map((rs) => (
                    <tr key={rs.resourceType} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                      <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                        {rs.resourceType}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                        {rs.capacityWU.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                        {rs.demandWU.toFixed(1)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${rs.remainingWU < 0 ? 'text-[var(--coral)] dark:text-[var(--coral)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}`}>
                        {rs.remainingWU.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span
                          className={
                            rs.utilizationPercent > 100
                              ? 'text-[var(--coral)] dark:text-[var(--coral)] font-bold'
                              : rs.utilizationPercent > 80
                                ? 'text-[var(--mustard)] dark:text-[var(--mustard)] font-medium'
                                : 'text-[var(--text-sec)] dark:text-[var(--text-pri)]'
                          }
                        >
                          {rs.utilizationPercent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Priority coverage table */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Priority Coverage
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                <thead className="bg-[var(--surface)] dark:bg-[var(--bg)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Required WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Included WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Deferred WU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Coverage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Feasibility</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Participation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {planOutput.prioritySummaries.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                        No priorities.
                      </td>
                    </tr>
                  )}
                  {planOutput.prioritySummaries.map((ps) => (
                    <tr key={ps.priority.id} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                      <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                        {ps.priority.name}
                        {ps.blockedReason && (
                          <p className="mt-0.5 text-xs font-normal text-[var(--coral)]">{ps.blockedReason}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                        {ps.requiredWU.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--green)] dark:text-[var(--text-ter)]">
                        {ps.includedWU.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--mustard)] dark:text-[var(--mustard)]">
                        {ps.deferredWU.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                        {ps.coveragePercent}%
                      </td>
                      <td className="px-4 py-3">
                        {feasibilityBadge(ps.feasibility)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {ps.hasParticipationRisk ? (
                          <span className="text-[var(--mustard)] dark:text-[var(--mustard)] font-medium">
                            {ps.decisionWithParticipation}
                          </span>
                        ) : (
                          <span className="text-[var(--text-ter)]">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}
